import { SlotButton } from '@avanta/shared/components/SlotButton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@avanta/shared/components/ui/Tooltip';
import { MODES } from '@avanta/shared/constants/common';
import { type TimeSlot } from '@avanta/shared/types/appointments';
import { type Mode } from '@avanta/shared/types/common';
import { cn } from '@avanta/shared/utils/cn';
import { extractTimeFromUnified, formatDateTimeUnified } from '@avanta/shared/utils/date';
import { CircleQuestionMark } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { AppointmentsReservePayload } from 'pages/Appointments/AppointmentsReserve/types';

interface SlotsGridProps {
  slots: TimeSlot[];
  title: string;
  subtitle?: string;
  selectedDate?: Date;
  mode: Mode;
  tooltip?: string;
}

export const SlotsGrid = ({
  slots,
  title,
  subtitle,
  selectedDate,
  mode,
  tooltip,
}: SlotsGridProps): React.ReactElement => {
  const { setValue, watch } = useFormContext();
  const dateTime = watch(AppointmentsReservePayload.dateTime);
  const selectedTime = dateTime ? extractTimeFromUnified(dateTime) : '';

  const handleOnSelectTime = (time: string): void => {
    const isDeselecting = time === selectedTime;
    setValue(
      AppointmentsReservePayload.dateTime,
      !isDeselecting && selectedDate ? formatDateTimeUnified(selectedDate, time) : ''
    );
  };
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p
            className={cn(
              'text-sm font-medium leading-4',
              mode === MODES.primary && 'text-teal-600',
              mode === MODES.secondary && 'text-denim-300'
            )}
          >
            {title}
          </p>
          {subtitle && <p className="text-sm text-gray-400 leading-4">{subtitle}</p>}
        </div>
        {tooltip && (
          <Tooltip>
            <TooltipTrigger>
              <CircleQuestionMark
                className={cn(
                  'size-4',
                  mode === MODES.primary && 'text-teal-600',
                  mode === MODES.secondary && 'text-denim-300'
                )}
              />
            </TooltipTrigger>
            <TooltipContent
              className="max-w-68 text-denim-500 text-xs leading-4"
              side="top"
              align="start"
              sideOffset={4}
              alignOffset={-20}
            >
              {tooltip}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      <div className="grid grid-cols-5 gap-2">
        {slots.map(({ time, disabled }) => (
          <SlotButton
            key={time}
            isActive={selectedTime === time}
            mode={mode}
            disabled={disabled}
            onClick={() => handleOnSelectTime(time)}
          >
            {time}
          </SlotButton>
        ))}
      </div>
    </div>
  );
};
